import { NextPage } from 'next';
import { VerticalAlt } from '../../types/contentstack';
import styled from 'styled-components';
import { getAssetUrl } from '../../utils/contentstack';
import { mediaScreen } from '../../utils/mediaScreen';
import { Col, ResponsiveContainer, Row } from '../responsive';
import {
  HeroHeadline,
  HeroSubheadline,
  SectionEyebrow,
  SectionHeadline,
  SectionSubheadline,
} from '../section';
import Link from 'next/link';
import { Button } from '../ui/button';
import { Accordion } from '../accordion';
import { OptimizedImage } from '../ui/optimizedImage';

export const VerticalAltPage: NextPage<{ page: VerticalAlt }> = ({ page }) => {
  return (
    <>
      <Hero>
        <div className="fade" />
        <div className="image">
          <OptimizedImage
            src={getAssetUrl(page?.hero?.imageConnection)}
            alt={page?.hero?.headline || ''}
            width={750}
            height={800}
          />
        </div>

        <ResponsiveContainer className="content">
          <SectionEyebrow>{page?.hero?.eyebrow}</SectionEyebrow>
          <HeroHeadline
            dangerouslySetInnerHTML={{ __html: page?.hero?.headline || '' }}
          />
          <HeroSubheadline
            dangerouslySetInnerHTML={{ __html: page?.hero?.subheadline || '' }}
          />

          <div className="buttons">
            <Link
              href={page?.hero?.primary_button?.href || ''}
              legacyBehavior
              passHref
            >
              <Button mType="primary">
                {page?.hero?.primary_button?.title}
              </Button>
            </Link>

            <Link
              href={page?.hero?.secondary_button?.href || ''}
              legacyBehavior
              passHref
            >
              <Button mType="secondary">
                {page?.hero?.secondary_button?.title}
              </Button>
            </Link>
          </div>
        </ResponsiveContainer>
      </Hero>

      {!!page?.section_1?.headline && (
        <Section1>
          <SectionEyebrow>{page?.section_1?.eyebrow}</SectionEyebrow>
          <SectionHeadline>{page?.section_1?.headline}</SectionHeadline>
          <SectionSubheadline>
            {page?.section_1?.subheadline}
          </SectionSubheadline>

          <div className="cards">
            {page?.section_1?.cards?.map((card, index) => (
              <div className="card" key={index}>
                <div className="image">
                  <OptimizedImage
                    src={getAssetUrl(card?.imageConnection)}
                    alt={card?.title || ''}
                    width={630}
                    height={318}
                  />
                </div>

                <div className="content">
                  <SectionEyebrow>{card?.eyebrow}</SectionEyebrow>
                  <SectionHeadline>{card?.title}</SectionHeadline>
                  <p className="text">{card?.content}</p>
                </div>
              </div>
            ))}
          </div>
        </Section1>
      )}

      {!!page?.section_2?.headline && (
        <Section2>
          <Row>
            <Col xs={12} md={7} className="texts">
              <SectionEyebrow>{page?.section_2?.eyebrow}</SectionEyebrow>
              <SectionHeadline>{page?.section_2?.headline}</SectionHeadline>
              <SectionSubheadline>
                {page?.section_2?.subheadline}
              </SectionSubheadline>
            </Col>

            <Col xs={12} md={5} className="image">
              <img src={getAssetUrl(page?.section_2?.imageConnection)} />
            </Col>
          </Row>

          <Cards>
            {page?.section_2?.cards?.map((card, index) => (
              <div className="card" key={index}>
                <img className="icon" src={getAssetUrl(card?.iconConnection)} />
                <h4 className="title">{card?.title}</h4>
                <p className="content">{card?.content}</p>
              </div>
            ))}
          </Cards>
        </Section2>
      )}

      {!!page?.section_3?.headline && (
        <Section3>
          <SectionEyebrow>{page?.section_3?.eyebrow}</SectionEyebrow>
          <SectionHeadline>{page?.section_3?.headline}</SectionHeadline>
          <SectionSubheadline>
            {page?.section_3?.subheadline}
          </SectionSubheadline>

          <div className="numbers">
            {page?.section_3?.numbers?.slice(0, 4).map((number, index) => (
              <div key={index} className={`number ${'number-' + (index + 1)}`}>
                <div className="image">
                  <img src={`/img/number-${index + 1}.svg`} />
                </div>

                <div className="texts">
                  <h4 className="title">{number?.title}</h4>
                  <p className="content">{number?.content}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="buttons">
            <p className="supporting-text">
              {page?.section_3?.supporting_text}
            </p>

            <Link
              href={page?.section_3?.primary_button?.href || ''}
              legacyBehavior
              passHref
            >
              <Button mType="primary">
                {page?.section_3?.primary_button?.title}
              </Button>
            </Link>

            <Link
              href={page?.section_3?.secondary_button?.href || ''}
              legacyBehavior
              passHref
            >
              <Button mType="secondary">
                {page?.section_3?.secondary_button?.title}
              </Button>
            </Link>
          </div>
        </Section3>
      )}

      {!!page?.section_4?.headline && (
        <Section4>
          <Row>
            <Col xs={12} md={7} className="image">
              <img src={getAssetUrl(page?.section_4?.imageConnection)} />
            </Col>

            <Col xs={12} md={5} className="texts">
              <SectionEyebrow>{page?.section_4?.eyebrow}</SectionEyebrow>
              <SectionHeadline>{page?.section_4?.headline}</SectionHeadline>
              <SectionSubheadline>
                {page?.section_4?.subheadline}
              </SectionSubheadline>

              <Link
                href={page?.section_4?.primary_button?.href || ''}
                legacyBehavior
                passHref
              >
                <Button mType="primary">
                  {page?.section_4?.primary_button?.title}
                </Button>
              </Link>
            </Col>
          </Row>
        </Section4>
      )}

      {!!page?.section_5?.headline && (
        <Section5>
          <SectionEyebrow>{page?.section_5?.eyebrow}</SectionEyebrow>
          <SectionHeadline>{page?.section_5?.headline}</SectionHeadline>
          <SectionSubheadline>
            {page?.section_5?.subheadline}
          </SectionSubheadline>

          <Cards>
            {page?.section_5?.cards?.map((card, index) => (
              <div className="card" key={index}>
                <img className="icon" src={getAssetUrl(card?.iconConnection)} />
                <h4 className="title">{card?.title}</h4>
                <p className="content">{card?.content}</p>
              </div>
            ))}
          </Cards>

          <div className="buttons">
            <Link
              href={page?.section_5?.primary_button?.href || ''}
              legacyBehavior
              passHref
            >
              <Button mType="primary">
                {page?.section_5?.primary_button?.title}
              </Button>
            </Link>

            <Link
              href={page?.section_5?.secondary_button?.href || ''}
              legacyBehavior
              passHref
            >
              <Button mType="secondary">
                {page?.section_5?.secondary_button?.title}
              </Button>
            </Link>
          </div>
        </Section5>
      )}

      {!!page?.section_6?.headline && (
        <Section6>
          <Row className="row">
            <Col xs={12} md={6} className="image">
              <img src={getAssetUrl(page?.section_6?.imageConnection)} />
            </Col>

            <Col xs={12} md={6} className="texts">
              <SectionEyebrow>{page?.section_6?.eyebrow}</SectionEyebrow>
              <SectionHeadline>{page?.section_6?.headline}</SectionHeadline>
              <SectionSubheadline
                dangerouslySetInnerHTML={{
                  __html: page?.section_6?.subheadline || '',
                }}
              />

              <div className="buttons">
                <Link
                  href={page?.section_6?.primary_button?.href || ''}
                  legacyBehavior
                  passHref
                >
                  <Button mType="primary">
                    {page?.section_6?.primary_button?.title}
                  </Button>
                </Link>

                <Link
                  href={page?.section_6?.secondary_button?.href || ''}
                  legacyBehavior
                  passHref
                >
                  <Button mType="secondary">
                    {page?.section_6?.secondary_button?.title}
                  </Button>
                </Link>
              </div>
            </Col>
          </Row>
        </Section6>
      )}

      {!!page?.section_7?.headline && (
        <Section7>
          <ResponsiveContainer>
            <SectionEyebrow>{page?.section_7?.eyebrow}</SectionEyebrow>
            <SectionHeadline>{page?.section_7?.headline}</SectionHeadline>
            <SectionSubheadline>
              {page?.section_7?.subheadline}
            </SectionSubheadline>

            <Cards>
              {page?.section_7?.cards?.map((card, index) => (
                <div className="card" key={index}>
                  <img
                    className="icon"
                    src={getAssetUrl(card?.iconConnection)}
                  />
                  <h4
                    className="title"
                    dangerouslySetInnerHTML={{ __html: card?.title || '' }}
                  />

                  <p className="content">{card?.content}</p>
                </div>
              ))}
            </Cards>

            <div className="buttons">
              <Link
                href={page?.section_7?.primary_button?.href || ''}
                legacyBehavior
                passHref
              >
                <Button mType="primary">
                  {page?.section_7?.primary_button?.title}
                </Button>
              </Link>
            </div>
          </ResponsiveContainer>
        </Section7>
      )}

      {!!page?.section_8?.headline && (
        <Section8>
          <ResponsiveContainer>
            <SectionEyebrow invert>{page?.section_8?.eyebrow}</SectionEyebrow>
            <SectionHeadline invert>
              {page?.section_8?.headline}
            </SectionHeadline>
            <SectionSubheadline invert>
              {page?.section_8?.subheadline}
            </SectionSubheadline>

            <Accordion
              invert
              items={page?.section_8?.accordion_items?.map((item) => ({
                title: item?.title || '',
                content: item?.content || '',
              }))}
            />

            <Link
              href={page?.section_8?.primary_button?.href || ''}
              legacyBehavior
              passHref
            >
              <Button mType="secondary" invert>
                {page?.section_8?.primary_button?.title}
              </Button>
            </Link>
          </ResponsiveContainer>
        </Section8>
      )}
    </>
  );
};

export const VERTICAL_ALT_PAGE_QUERY = /**gql */ `
  all_vertical_alt(where: { url: $url }) {
    items {
      title
      description
      url
      hero {
        eyebrow
        headline
        subheadline
        primary_button {
          title
          href
        }
        secondary_button {
          title
          href
        }
        imageConnection {
          edges {
            node {
              url
            }
          }
        }
      }
      section_1 {
        eyebrow
        headline
        subheadline
        cards {
          eyebrow
          title
          content
          imageConnection {
            edges {
              node {
                url
              }
            }
          }
        }
      }
      section_2 {
        eyebrow
        headline
        subheadline
        imageConnection {
          edges {
            node {
              url
            }
          }
        }
        cards {
          title
          content
          iconConnection {
            edges {
              node {
                url
              }
            }
          }
        }
      }
      section_3 {
        eyebrow
        headline
        subheadline
        supporting_text
        numbers {
          title
          content
        }
        primary_button {
          title
          href
        }
        secondary_button {
          title
          href
        }
      }
      section_4 {
        eyebrow
        headline
        subheadline
        imageConnection {
          edges {
            node {
              url
            }
          }
        }
        primary_button {
          title
          href
        }
      }
      section_5 {
        eyebrow
        headline
        subheadline
        cards {
          title
          content
          iconConnection {
            edges {
              node {
                url
              }
            }
          }
        }
        primary_button {
          title
          href
        }
        secondary_button {
          title
          href
        }
      }
      section_6 {
        eyebrow
        headline
        subheadline
        imageConnection {
          edges {
            node {
              url
            }
          }
        }
        primary_button {
          title
          href
        }
        secondary_button {
          title
          href
        }
      }
      section_7 {
        eyebrow
        headline
        subheadline
        cards {
          title
          content
          iconConnection {
            edges {
              node {
                url
              }
            }
          }
        }
        primary_button {
          title
          href
        }
      }
      section_8 {
        eyebrow
        headline
        subheadline
        accordion_items {
          title
          content
        }
        primary_button {
          title
          href
        }
      }
      footer {
        headline
        subheadline
        primary_button {
          title
          href
        }
        secondary_button {
          title
          href
        }
        third_button {
          title
          href
        }
      }
    }
  }
`;

const Hero = styled.section`
  background-size: cover;
  min-height: 760px;
  overflow: hidden;
  position: relative;
  max-width: 1600px;
  width: 100%;
  margin: 0 auto;

  .image {
    pointer-events: none;
    position: absolute;
    bottom: 0;
    right: 0;
    width: 50%;
    height: 100%;

    div,
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .fade {
    pointer-events: none;
    position: absolute;
    height: 100%;
    width: 40%;
    z-index: 2;
    left: 49%;
    background: linear-gradient(90deg, #181e2b 0%, #181e2b00 90%);
  }

  .content {
    position: relative;
    z-index: 3;
    padding-top: 180px;
    padding-bottom: 160px;

    ${HeroHeadline} {
      max-width: 700px;
    }

    ${HeroSubheadline} {
      max-width: 500px;
    }
  }

  .buttons {
    display: flex;
    gap: 24px;
  }

  ${mediaScreen('xs', 'md')} {
    min-height: auto;

    ${HeroHeadline}, ${HeroSubheadline} {
      max-width: 100%;
    }

    .fade {
      width: 70%;
    }

    .content {
      padding-top: 100px;
      padding-bottom: 60px;
    }

    .buttons {
      flex-direction: column;
      gap: 16px;

      .mButton {
        display: flex;
      }
    }
  }
`;

const Cards = styled.div`
  display: flex;
  gap: 24px;
  margin-top: 60px;

  .card {
    flex: 1;
    display: flex;
    align-items: flex-start;
    flex-direction: column;
    background: ${(p) => p.theme.colors.slate2};
    padding: 24px;
    border-radius: 8px;

    .icon {
      height: 50px;
      width: auto;
      margin-bottom: 24px;
    }

    .title {
      font-size: 28px;
      margin: 0 0 16px;
    }

    .content {
      font-size: 16px;
      margin: 0;
    }
  }

  ${mediaScreen('xs', 'md')} {
    margin-top: 40px;
    flex-direction: column;
  }
`;

const Section1 = styled(ResponsiveContainer)`
  padding-top: 60px;
  padding-bottom: 60px;

  > ${SectionEyebrow}, > ${SectionHeadline}, > ${SectionSubheadline} {
    text-align: center;
    margin-left: auto;
    margin-right: auto;
    display: block;
    max-width: 780px;
  }

  ${SectionEyebrow} {
    margin-top: 0;
  }

  .cards {
    display: flex;
    flex-wrap: nowrap;
    gap: 24px;
    margin-top: 60px;

    .card {
      width: 50%;
      background: ${(p) => p.theme.colors.slate2};
      padding: 24px;
      border-radius: 8px;
      display: flex;
      flex-direction: column;

      .image {
        width: 100%;
        height: 75%;
        overflow: hidden;
        border-radius: 8px;
        margin-bottom: 24px;

        div,
        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
      }

      .content {
        flex: 1;
      }

      ${SectionEyebrow} {
        margin-bottom: 16px;
      }

      ${SectionHeadline} {
        font-size: 28px;
        margin-top: 0;
        margin-bottom: 16px;
      }

      .text {
        font-size: 16px;
        margin: 0;
      }
    }
  }

  ${mediaScreen('xs', 'md')} {
    padding-top: 60px;

    .cards {
      flex-direction: column;
      flex-wrap: wrap;

      .card {
        width: 100%;
      }
    }
  }
`;

const Section2 = styled(ResponsiveContainer)`
  padding-bottom: 50px;

  .texts,
  .image {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .image {
    align-items: flex-end;
    img {
      max-width: 80%;
    }
  }

  ${mediaScreen('xs', 'md')} {
    .row {
      flex-direction: column-reverse;

      .image {
        align-items: flex-start;
        margin-bottom: 16px;
        margin-top: -24px;
      }
    }
  }
`;

const Section3 = styled(ResponsiveContainer)`
  padding-top: 80px;
  padding-bottom: 80px;

  ${SectionEyebrow}, ${SectionHeadline}, ${SectionSubheadline} {
    display: block;
    text-align: center;
    margin-left: auto;
    margin-right: auto;
  }

  .numbers {
    display: flex;
    justify-content: space-around;
    margin-top: 60px;

    &:hover {
      .number {
        opacity: 0.3;
      }
    }

    .number {
      display: flex;
      position: relative;
      cursor: default !important;

      &,
      &::after,
      .texts {
        transition: opacity 0.25s ease;
      }

      &::after {
        content: '';
        opacity: 0;
        position: absolute;
        pointer-events: none;
        top: 0;
        bottom: 0;
        width: 100%;
        z-index: 2;
        right: 0;
        background: linear-gradient(270deg, #181e2b 0%, #181e2b00 90%);
      }

      .image {
        height: 370px;
      }

      img {
        height: 100%;
      }

      .texts {
        position: absolute;
        opacity: 0;
        z-index: 3;
        top: 32px;
        width: 300px;
        pointer-events: none;

        .title {
          font-size: 22px;
          font-weight: 700;
          margin: 0;
          margin-bottom: 16px;
        }

        .content {
          font-size: 16px;
          line-height: 1.5;
          margin: 0;
        }
      }

      &.number-1 .texts {
        left: 85%;
        .title {
          color: ${(p) => p.theme.colors.teal};
        }
      }
      &.number-2 .texts {
        left: 80%;
        .title {
          color: ${(p) => p.theme.colors.yellow};
        }
      }
      &.number-3 .texts {
        left: 78%;
        .title {
          color: ${(p) => p.theme.colors.red};
        }
      }
      &.number-4 {
        &::after {
          right: auto;
          left: 0;
          background: linear-gradient(90deg, #181e2b 0%, #181e2b00 90%);
        }

        .texts {
          right: 55%;

          .title {
            color: ${(p) => p.theme.colors.eb2};
          }
        }
      }

      &:hover {
        &,
        &::after,
        .texts {
          opacity: 1 !important;
        }
      }
    }
  }

  .buttons {
    display: flex;
    width: 100%;
    justify-content: center;
    align-items: center;
    gap: 24px;
    margin-top: 60px;

    .supporting-text {
      margin: 0;
      margin-right: 20px;
      font-size: 20px;
      line-height: 1.5;
    }
  }

  ${mediaScreen('xs', 'md')} {
    padding-top: 0px;
    padding-bottom: 20px;

    ${SectionEyebrow}, ${SectionHeadline}, ${SectionSubheadline} {
      text-align: left;
      margin-left: 0;
      margin-right: 0;
    }
    .numbers {
      flex-direction: column;
      pointer-events: none !important;
      margin-left: -32px;

      .number {
        margin-bottom: 44px;
        margin-left: -45px;
        height: auto;

        .image {
          width: 161px;
          height: 188px;
          margin-right: -32px;

          img {
            max-width: none;
            margin: 0 auto;
          }
        }

        .texts {
          flex: 1;
          opacity: 1;
          top: 16px;
          left: 0 !important;
          position: relative;
        }

        &.number-1 .image img {
          position: relative;
          left: -10px;
        }
      }
    }
    .buttons {
      flex-direction: column;
      align-items: flex-start;
      justify-content: flex-start;
      margin-top: 20px;

      > * {
        width: 100%;
      }
    }
  }
`;

const Section4 = styled(ResponsiveContainer)`
  padding-top: 80px;
  padding-bottom: 80px;

  .row {
    align-items: center;
    gap: 44px;
    flex-wrap: nowrap;
  }

  ${SectionEyebrow} {
    margin-top: 0;
  }

  .image {
    img {
      width: 130%;
      max-width: 130%;
      left: -30%;
      position: relative;
    }
  }

  .mButton {
    margin-top: 32px;
  }

  ${mediaScreen('xs', 'md')} {
    .row {
      flex-direction: column;
    }

    .image img {
      width: 100%;
      left: 0;
    }

    .mButton {
      width: 100%;
    }
  }
`;

const Section5 = styled(ResponsiveContainer)`
  padding-bottom: 80px;

  > ${SectionEyebrow}, > ${SectionHeadline}, > ${SectionSubheadline} {
    display: block;
    text-align: center;
    margin-left: auto;
    margin-right: auto;
  }

  .buttons {
    width: 100%;
    display: flex;
    gap: 24px;
    align-items: center;
    justify-content: center;
    margin-top: 44px;
  }

  ${mediaScreen('xs', 'md')} {
    > ${SectionEyebrow}, > ${SectionHeadline}, > ${SectionSubheadline} {
      text-align: left;
      margin-left: 0;
      margin-right: 0;
    }

    .buttons {
      flex-direction: column;
      .button {
        width: 100%;
      }
    }
  }
`;

const Section6 = styled(ResponsiveContainer)`
  padding-top: 80px;
  padding-bottom: 80px;

  .row {
    display: flex;
    align-items: center;
    gap: 100px;
    flex-wrap: nowrap;
  }

  ${SectionEyebrow} {
    margin-top: 0;
  }

  .buttons {
    display: flex;
    gap: 24px;
    margin-top: 40px;
  }

  ${mediaScreen('xs', 'md')} {
    padding-top: 0px;

    .row {
      flex-direction: column;
      gap: 32px;
    }

    .buttons {
      flex-direction: column;
      width: 100%;

      .mButton {
        width: 100%;
      }
    }
  }
`;

const Section7 = styled.section`
  padding-top: 80px;
  padding-bottom: 80px;
  background-image: url('/img/dots-bg-alt-2.svg');
  background-repeat: no-repeat;
  background-position: bottom right;
  background-size: 100%;

  ${SectionEyebrow}, ${SectionHeadline}, ${SectionSubheadline} {
    display: block;
    text-align: center;
    margin-left: auto;
    margin-right: auto;
  }

  .buttons {
    width: 100%;
    display: flex;
    gap: 24px;
    align-items: center;
    justify-content: center;
    margin-top: 44px;
  }

  .card {
    .title {
      font-size: 40px;
      font-weight: 400;
      margin-bottom: 0;

      b {
        font-weight: 400;
        color: ${(p) => p.theme.colors.teal};
      }
    }
  }

  ${mediaScreen('xs', 'md')} {
    padding-bottom: 40px;
    padding-top: 0;
    background-size: 250%;

    ${SectionEyebrow}, ${SectionHeadline}, ${SectionSubheadline} {
      text-align: left;
      margin-left: 0;
      margin-right: 0;
    }

    ${Cards} {
      flex-direction: row;
      flex-wrap: wrap;
      gap: 16px;
    }

    .card {
      width: calc(50% - 8px);

      .icon {
        display: none;
      }

      .title {
        font-size: 28px;
      }
    }

    .buttons {
      flex-direction: column;
      width: 100%;

      .mButton {
        width: 100%;
      }
    }
  }
`;

const Section8 = styled.section`
  padding-top: 80px;
  padding-bottom: 80px;
  background: white;

  ${SectionEyebrow}, ${SectionHeadline}, ${SectionSubheadline} {
    display: block;
    text-align: center;
    margin-left: auto;
    margin-right: auto;
  }

  .accordion {
    margin-top: 60px;
    max-width: 700px;
    margin-left: auto;
    margin-right: auto;
  }

  .mButton {
    margin: 60px auto;
    margin-bottom: 0;
    display: flex;
    justify-self: center;
  }

  ${mediaScreen('xs', 'md')} {
    padding-top: 20px;
    padding-bottom: 40px;

    ${SectionEyebrow}, ${SectionHeadline}, ${SectionSubheadline} {
      text-align: left;
      margin-left: 0;
      margin-right: 0;
    }

    .accordion {
      margin-top: 30px;
    }

    .mButton {
      margin-top: 40px;
      width: 100%;
    }
  }
`;
